import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiCheck, FiSend, FiThumbsDown, FiRefreshCw, FiEdit3, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Auth } from 'aws-amplify';
import Loading from '../../ions/Loading';
import FileUpload from '../../molecules/FileUpload';
import SelectFilter from '../../organisms/SelectFilter';
import { signatureApi } from '../../../services/signatureApi';
import appConfig from '../../../config/app-config';
import { ProviderOption } from './types';

const getCurrentUser = async (): Promise<string> => {
    try {
        const session = await Auth.currentSession();
        return session.getIdToken().payload.email || session.getIdToken().payload.sub || 'unknown';
    } catch {
        return 'unknown';
    }
};

type Step = 'upload' | 'extraction' | 'confirm' | 'send';

const formatCpfCnpj = (value: string | null | undefined): string => {
    if (!value) return '-';
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return value;
};

const STEPS: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: 'upload', label: 'Upload', icon: <FiUpload size={14} /> },
    { key: 'extraction', label: 'Extração', icon: <FiCheck size={14} /> },
    { key: 'confirm', label: 'Confirmação', icon: <FiCheck size={14} /> },
    { key: 'send', label: 'Envio', icon: <FiSend size={14} /> },
];

const DISPLAY_FIELDS: { key: string; label: string }[] = [
    { key: 'person_name', label: 'Nome/Razão Social' },
    { key: 'person_gov_tax_id', label: 'CPF/CNPJ' },
    { key: 'person_type', label: 'Tipo Pessoa' },
    { key: 'operation_type', label: 'Tipo Operação' },
    { key: 'operation_value', label: 'Valor da Operação' },
];

const SignatureCreatePage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('upload');
    const [globalLoading, setGlobalLoading] = useState(false);

    // Upload
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState('');
    const [operationNumber, setOperationNumber] = useState('');
    const [documentUuid, setDocumentUuid] = useState('');
    const [s3Key, setS3Key] = useState('');

    // Extraction
    const [extractionId, setExtractionId] = useState('');
    const [extractedData, setExtractedData] = useState<Record<string, unknown> | null>(null);
    const [attemptNumber, setAttemptNumber] = useState(0);
    const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
    const [negativeFeedbacks, setNegativeFeedbacks] = useState<Set<string>>(new Set());
    const [editedFields, setEditedFields] = useState<Record<string, string>>({});

    // Send
    const [providerId, setProviderId] = useState<string>('');
    const [providers, setProviders] = useState<ProviderOption[]>([]);

    // Signer validation
    const [signerValidation, setSignerValidation] = useState<{ valid: boolean; signers: { name: string; govTaxId: string; role: string; valid: boolean; email?: string; phone?: string; reason?: string }[] } | null>(null);
    const [validatingSigners, setValidatingSigners] = useState(false);

    // Delivery channels
    const [sendWhatsApp, setSendWhatsApp] = useState(false);
    const [sendSms, setSendSms] = useState(false);

    // Observers
    const [observers, setObservers] = useState<{ personUuid: string; name: string; email: string; socialId?: string }[]>([]);
    const [observerSearch, setObserverSearch] = useState('');
    const [observerResults, setObserverResults] = useState<any[]>([]);
    const [searchingObservers, setSearchingObservers] = useState(false);

    const searchObservers = async () => {
        if (!observerSearch) return;
        setSearchingObservers(true);
        try {
            const { Auth } = await import('aws-amplify');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const personApi = appConfig.apis.personApiEnv;
            const res = await fetch(
                `${personApi.address}/individual/page-list?name=${encodeURIComponent(observerSearch.replace(/\D/g, '') || observerSearch)}&email=&mobile=&page=0&size=10&sort=id,DESC`,
                { headers: { 'x-api-key': personApi.key, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (res.ok) {
                const data = await res.json();
                const list = data.list || [];
                setObserverResults(list);
                if (list.length === 0) {
                    toast.info('Nenhuma pessoa encontrada com esse CPF/nome');
                }
            } else {
                toast.error('Erro ao buscar pessoa. Tente novamente.');
            }
        } catch {
            toast.error('Não foi possível conectar ao serviço de pessoas');
        } finally {
            setSearchingObservers(false);
        }
    };

    const addObserver = (person: any) => {
        const personUuid = person.uuid || person.id;
        if (observers.some(o => o.personUuid === personUuid)) return;
        setObservers(prev => [...prev, {
            personUuid,
            name: person.name || person.fullName || '',
            email: person.email || '',
            socialId: person.socialId || person.gov_tax_id || '',
        }]);
        setObserverResults([]);
        setObserverSearch('');
    };

    const goToConfirmStep = async () => {
        setStep('confirm');
        setValidatingSigners(true);
        setSignerValidation(null);
        try {
            const res = await signatureApi.validateSigners(documentUuid);
            setSignerValidation(res.data || res);
        } catch (e: any) {
            toast.error('Erro ao validar signatários');
        } finally {
            setValidatingSigners(false);
        }
    };

    useEffect(() => {
        signatureApi.getProviders().then((res: { data: ProviderOption[] }) => {
            const active = res.data.filter((p: ProviderOption) => p.isActive);
            setProviders(active);
            if (active.length > 0) setProviderId(active[0].providerId);
        }).catch(() => toast.error('Erro ao carregar fornecedores'));
    }, []);

    const handleUpload = async () => {
        if (!file || !operationNumber) { toast.warn('Selecione um PDF e informe o número da operação'); return; }
        setGlobalLoading(true);
        try {
            const reader = new FileReader();
            const base64 = await new Promise<string>((resolve) => {
                reader.onload = () => resolve((reader.result as string).split(',')[1]);
                reader.readAsDataURL(file);
            });
            const result = await signatureApi.uploadDocument(file.name, base64, operationNumber, await getCurrentUser());
            setDocumentUuid(result.documentUuid);
            setS3Key(result.s3Key);
            toast.success('Documento enviado');
            await doExtract(result.documentUuid, result.s3Key);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao enviar documento');
        } finally {
            setGlobalLoading(false);
        }
    };

    const doExtract = async (docId?: string, key?: string) => {
        setGlobalLoading(true);
        try {
            const result = await signatureApi.extractDocument(docId || documentUuid, key || s3Key);
            setExtractionId(result.extractionId);
            setExtractedData(result.extractedData);
            setAttemptNumber(result.attemptNumber);
            setMaxAttemptsReached(result.maxAttemptsReached);
            setNegativeFeedbacks(new Set());
            setStep('extraction');
        } catch (e: any) {
            toast.error(e.message || 'Erro na extração');
        } finally {
            setGlobalLoading(false);
        }
    };

    const toggleFeedback = (field: string) => {
        setNegativeFeedbacks(prev => { const n = new Set(prev); n.has(field) ? n.delete(field) : n.add(field); return n; });
    };

    const handleRetry = async () => {
        if (negativeFeedbacks.size === 0) return;
        setGlobalLoading(true);
        try {
            const feedbacks = Array.from(negativeFeedbacks).map(f => ({ field: f, extractedValue: String(extractedData?.[f] || '') }));
            await signatureApi.sendFeedback(documentUuid, extractionId, feedbacks);
            const result = await signatureApi.retryExtraction(documentUuid);
            setExtractionId(result.extractionId);
            setExtractedData(result.extractedData);
            setAttemptNumber(result.attemptNumber);
            setMaxAttemptsReached(result.maxAttemptsReached);
            setNegativeFeedbacks(new Set());
            toast.info(`Re-extração (tentativa ${result.attemptNumber}/3)`);
        } catch (e: any) {
            toast.error(e.message || 'Erro na re-extração');
        } finally {
            setGlobalLoading(false);
        }
    };

    const handleConfirm = async () => {
        setGlobalLoading(true);
        try {
            await signatureApi.confirmExtraction(documentUuid, Object.keys(editedFields).length > 0 ? editedFields : undefined, await getCurrentUser());
            toast.success('Dados confirmados');
            setStep('send');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao confirmar');
        } finally {
            setGlobalLoading(false);
        }
    };

    const handleSend = async () => {
        setGlobalLoading(true);
        try {
            const result = await signatureApi.createProcess({ documentUuid, operationNumber, providerId, observers });
            toast.success(`Processo(s) criado(s): ${result.length}`);
            if (result.length > 0) {
                const channels = { whatsapp: sendWhatsApp, sms: sendSms };
                for (const proc of result) {
                    await signatureApi.sendProcess(proc.signatureProcessUuid, channels);
                }
                toast.success('Enviado(s) para assinatura!');
                navigate(`/signature/${result[0].signatureProcessUuid}`);
            }
        } catch (e: any) {
            toast.error(e.message || 'Erro ao enviar');
        } finally {
            setGlobalLoading(false);
        }
    };

    const canRetry = negativeFeedbacks.size > 0 && !maxAttemptsReached;
    const isEditable = maxAttemptsReached && negativeFeedbacks.size > 0;
    const stepIndex = STEPS.findIndex(s => s.key === step);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {globalLoading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-4xl mx-auto">
                    <button onClick={() => navigate('/signature')} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Nova Assinatura Digital</h1>
                </div>
            </div>

            {/* Step indicator */}
            <div className="max-w-4xl mx-auto px-6 py-4">
                <div className="flex gap-1">
                    {STEPS.map((s, i) => (
                        <div key={s.key} className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-lg transition-colors ${
                            i < stepIndex ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            i === stepIndex ? 'bg-blue-600 text-white' :
                            'bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-gray-500'
                        }`}>
                            {s.icon} {s.label}
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 pb-8">
                {/* Step 1: Upload */}
                {step === 'upload' && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><FiUpload /> Upload do Documento</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número da Operação</label>
                                <input type="text" value={operationNumber} onChange={e => setOperationNumber(e.target.value)}
                                    placeholder="Ex: OP-2024-001"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <FileUpload
                                id="signature-pdf"
                                name="document"
                                label="Arquivo enviado:"
                                accept=".pdf"
                                info="Somente arquivos PDF"
                                errorMessage={fileError}
                                onUploadFile={(f) => { setFile(f); setFileError(''); }}
                                onRemoveFile={() => setFile(null)}
                            />
                            <button onClick={handleUpload} disabled={globalLoading || !file || !operationNumber}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                Enviar e Extrair Dados
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Extraction */}
                {step === 'extraction' && extractedData && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dados Extraídos (Tentativa {attemptNumber}/3)</h2>
                            {canRetry && (
                                <button onClick={handleRetry} disabled={globalLoading}
                                    className="flex items-center gap-1 px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                                    <FiRefreshCw /> Extrair Novamente
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Clique em <FiThumbsDown className="inline text-red-500" /> se algum dado estiver incorreto.
                            {maxAttemptsReached && ' Limite atingido — campos marcados podem ser editados.'}
                        </p>

                        <div className="space-y-3">
                            {DISPLAY_FIELDS.map(f => {
                                const value = extractedData[f.key];
                                const hasNeg = negativeFeedbacks.has(f.key);
                                const editable = isEditable && hasNeg;
                                return (
                                    <div key={f.key} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                        hasNeg ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20' : 'border-gray-200 dark:border-slate-700'}`}>
                                        <div className="flex-1">
                                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.label}</label>
                                            {editable ? (
                                                <div className="flex items-center gap-2 mt-1">
                                                    <FiEdit3 className="text-blue-500" size={14} />
                                                    <input type="text" value={editedFields[f.key] ?? String(value || '')}
                                                        onChange={e => setEditedFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                                                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                            ) : (
                                                <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{f.key === 'person_gov_tax_id' ? formatCpfCnpj(String(value || '')) : String(value || '-')}</p>
                                            )}
                                        </div>
                                        <button onClick={() => toggleFeedback(f.key)}
                                            className={`p-2 rounded-lg transition-colors ${hasNeg ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30'}`}>
                                            <FiThumbsDown size={16} />
                                        </button>
                                    </div>
                                );
                            })}

                            {/* Signers */}
                            <div className="mt-6">
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Signatários Extraídos</h3>
                                <div className="space-y-2">
                                    {(extractedData.signers as { name: string; gov_tax_id?: string; role: string }[] || []).map((s, i) => (
                                        <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-slate-800 rounded-lg text-sm">
                                            <span className="font-medium text-gray-900 dark:text-white">{s.name}</span>
                                            <span className="text-gray-500 dark:text-gray-400">{formatCpfCnpj(s.gov_tax_id)}</span>
                                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">{s.role}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setStep('upload')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={goToConfirmStep} disabled={negativeFeedbacks.size > 0 && !maxAttemptsReached}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                {negativeFeedbacks.size === 0 ? 'Confirmar Dados' : maxAttemptsReached ? 'Confirmar com Edições' : 'Resolva os feedbacks'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Confirm */}
                {step === 'confirm' && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configuração do Envio</h2>

                        {/* Extracted Signers with validation status */}
                        {extractedData && Array.isArray(extractedData.signers) && (
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Signatários ({(extractedData.signers as any[]).length})</h3>
                                {validatingSigners && <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Validando signatários...</p>}
                                <div className="space-y-2">
                                    {(extractedData.signers as { name: string; gov_tax_id?: string; role: string; signer_order?: number }[]).map((s, i) => {
                                        const validation = signerValidation?.signers?.find(v => v.govTaxId === s.gov_tax_id);
                                        return (
                                            <div key={i} className={`flex flex-col gap-1 p-3 rounded-lg text-sm border ${
                                                validation ? (validation.valid ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20') : 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800'
                                            }`}>
                                                <div className="flex items-center gap-3">
                                                    {validation && (
                                                        <span className={`text-lg ${validation.valid ? 'text-green-600' : 'text-red-600'}`}>
                                                            {validation.valid ? '👍' : '👎'}
                                                        </span>
                                                    )}
                                                    <span className="text-gray-400 dark:text-gray-500 w-5">{s.signer_order || i + 1}.</span>
                                                    <span className="font-medium text-gray-900 dark:text-white">{s.name}</span>
                                                    <span className="text-gray-500 dark:text-gray-400">{formatCpfCnpj(s.gov_tax_id)}</span>
                                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs">{s.role}</span>
                                                </div>
                                                {validation && validation.valid && (
                                                    <div className="flex gap-4 ml-10 text-xs text-gray-600 dark:text-gray-400">
                                                        {validation.email && <span>📧 {validation.email}</span>}
                                                        {validation.phone && <span>📱 {validation.phone}</span>}
                                                    </div>
                                                )}
                                                {validation && !validation.valid && (
                                                    <span className="ml-10 text-xs text-red-600 dark:text-red-400">{validation.reason}</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Observers */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observadores (opcional)</label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Pessoas que receberão notificações sobre este processo de assinatura.</p>
                            <div className="flex gap-2 mb-3">
                                <input type="text" value={observerSearch} onChange={e => setObserverSearch(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') searchObservers(); }}
                                    placeholder="Buscar por nome ou CPF..."
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500" />
                                <button onClick={searchObservers} disabled={!observerSearch || searchingObservers}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                    {searchingObservers ? 'Buscando...' : 'Buscar'}
                                </button>
                            </div>

                            {/* Search results */}
                            {observerResults.length > 0 && (
                                <div className="border border-gray-200 dark:border-slate-700 rounded-lg mb-3 max-h-40 overflow-y-auto">
                                    {observerResults.map((p: any) => (
                                        <button key={p.uuid} onClick={() => addObserver(p)}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700 last:border-0">
                                            <span className="font-medium text-gray-900 dark:text-white">{p.name}</span>
                                            <span className="ml-2 text-gray-500 dark:text-gray-400">{p.socialId || ''}</span>
                                            <span className="ml-2 text-gray-400 dark:text-gray-500">{p.email || ''}</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Selected observers */}
                            {observers.length > 0 && (
                                <div className="space-y-2 mt-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{observers.length} observador(es) selecionado(s):</p>
                                    {observers.map((o, i) => (
                                        <div key={i} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded-lg text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900 dark:text-white">{o.name}</span>
                                                {o.socialId && <span className="text-gray-500 dark:text-gray-400">{formatCpfCnpj(o.socialId)}</span>}
                                                {o.email && <span className="text-gray-400 dark:text-gray-500 text-xs">{o.email}</span>}
                                            </div>
                                            <button onClick={() => setObservers(prev => prev.filter((_, idx) => idx !== i))}
                                                className="text-red-500 hover:text-red-700 text-xs">Remover</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Provider */}
                        <div className="mb-4">
                            <SelectFilter
                                id="provider-select"
                                label="Fornecedor de Assinatura"
                                value={providerId}
                                onChange={(value) => setProviderId(value)}
                                options={providers.map(p => ({ value: p.providerId, label: p.displayName || p.name }))}
                            />
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setStep('extraction')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={handleConfirm} disabled={validatingSigners || (signerValidation !== null && !signerValidation.valid)}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                {validatingSigners ? 'Validando...' : 'Confirmar e Prosseguir'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 4: Send */}
                {step === 'send' && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><FiSend /> Enviar para Assinatura</h2>
                        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-4 text-sm space-y-1 text-gray-700 dark:text-gray-300">
                            <p><strong>Operação:</strong> {operationNumber}</p>
                            <p><strong>Documento:</strong> {file?.name}</p>
                            <p><strong>Fornecedor:</strong> {providers.find(p => p.providerId === providerId)?.displayName || providerId}</p>
                        </div>

                        <div className="mb-4">
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Canais de Notificação</h3>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <input type="checkbox" checked disabled className="rounded" />
                                    📧 Email (obrigatório)
                                </label>
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <input type="checkbox" checked={sendWhatsApp} onChange={e => setSendWhatsApp(e.target.checked)}
                                        className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" />
                                    📱 WhatsApp (adicional)
                                </label>
                                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                    <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)}
                                        className="rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500" />
                                    💬 SMS (adicional)
                                </label>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Signatários sempre recebem por email. Canais adicionais requerem telefone cadastrado.</p>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setStep('confirm')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={handleSend} disabled={globalLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                Enviar para Assinatura
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SignatureCreatePage;
